import { recordChangedSnackbar } from "../readmission-f2f-utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import useCurrentUser from "@/hooks/local-storage/use-current-user";
import { useQueryParams } from "@/hooks/use-query-param";
import { useSnackbar } from "@/hooks/useSnackbar";
import { usePatientMedicationCreate } from "@/queries/medication/use-patient-medication-create";
import { usePatientMedicationList } from "@/queries/medication/use-patient-medication-list";
import { usePatientMedicationUpdate } from "@/queries/medication/use-patient-medication-update";
import { useArchiveMedication } from "@/queries/medication/use-patient-medication-archive";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "@/utils/utils";
import {
  MEDICATION_RECORD_NAME,
  MEDICATION_LIST_PAGE_SIZE,
  PATIENT_MEDICATION_LIST_KEY,
} from "../readmission-f2f-medication-constant";
import { MedicationEntry, PrescriberOption } from "../readmission-f2f-types";
import {
  buildMedicationPayload,
  createMedicationEntry,
  isMedicationEntryComplete,
  isSameMedicationEntry,
  toMedicationEntries,
} from "../readmission-f2f-medication-utils";

export interface UseMedicationSection {
  rows: MedicationEntry[];
  draft: MedicationEntry;
  isLoading: boolean;
  isSaving: boolean;
  updateRow: (id: string, patch: Partial<MedicationEntry>) => void;
  updateDraft: (patch: Partial<MedicationEntry>) => void;
  addDraft: () => Promise<void>;
  saveRow: (row: MedicationEntry) => Promise<void>;
  removeRow: (row: MedicationEntry) => void;
  isRowChanged: (row: MedicationEntry) => boolean;
}

/** Medications for the appointment: listed from, created, updated and archived through the patient-medication API. */
export const useMedicationSection = (
  isEnabled: boolean
): UseMedicationSection => {
  const { patientId } = useParams() as { patientId?: string };
  const appointmentId = useQueryParams("appointmentid");
  const xTENANTID = getXtenantId();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();
  const [me] = useCurrentUser();

  const { mutateAsync: createMedication, isPending: isCreating } =
    usePatientMedicationCreate();
  const { mutateAsync: updateMedication, isPending: isUpdating } =
    usePatientMedicationUpdate();
  const { mutateAsync: archiveMedication, isPending: isArchiving } =
    useArchiveMedication();

  const defaultPrescriber = useMemo<PrescriberOption | null>(
    () =>
      me?.uuid
        ? {
            value: me.uuid,
            label: `${me.firstName ?? ""} ${me.lastName ?? ""}`.trim(),
          }
        : null,
    [me?.uuid, me?.firstName, me?.lastName]
  );

  const { data, isLoading } = usePatientMedicationList({
    patientUuid: appointmentId ? undefined : patientId,
    appointmentUuid: appointmentId ?? undefined,
    pagination: { page: 0, pageSize: MEDICATION_LIST_PAGE_SIZE },
    gridSorting: [],
    archive: false,
    enabled: isEnabled,
  });

  const serverRows = useMemo(
    () => toMedicationEntries(data?.data?.content),
    [data?.data?.content]
  );
  const serverById = useMemo(
    () => new Map(serverRows.map((row) => [row.id, row])),
    [serverRows]
  );

  const [rows, setRows] = useState<MedicationEntry[]>([]);
  const lastSignature = useRef<string | null>(null);
  useEffect(() => {
    const signature = JSON.stringify(serverRows);
    if (lastSignature.current === signature) return;
    lastSignature.current = signature;
    setRows(serverRows);
  }, [serverRows]);

  const [draft, setDraft] = useState<MedicationEntry>(() =>
    createMedicationEntry(defaultPrescriber)
  );

  const invalidate = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: [PATIENT_MEDICATION_LIST_KEY],
      }),
    [queryClient]
  );

  const updateRow = useCallback(
    (id: string, patch: Partial<MedicationEntry>) =>
      setRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
      ),
    []
  );

  const updateDraft = useCallback(
    (patch: Partial<MedicationEntry>) =>
      setDraft((prev) => ({ ...prev, ...patch })),
    []
  );

  const addDraft = useCallback(async () => {
    if (!isMedicationEntryComplete(draft)) return;
    try {
      await createMedication({
        patientMedication: buildMedicationPayload(
          draft,
          patientId,
          appointmentId ?? undefined
        ),
        xTENANTID,
      });
      showSnackbar(recordChangedSnackbar(MEDICATION_RECORD_NAME, "added"));
      setDraft(createMedicationEntry(defaultPrescriber));
      await invalidate();
    } catch (err) {
      showSnackbar(getErrorSnackbar(err));
    }
  }, [
    draft,
    patientId,
    appointmentId,
    xTENANTID,
    defaultPrescriber,
    createMedication,
    invalidate,
    showSnackbar,
  ]);

  const isRowChanged = useCallback(
    (row: MedicationEntry) => {
      const original = serverById.get(row.id);
      return !!original && !isSameMedicationEntry(original, row);
    },
    [serverById]
  );

  const saveRow = useCallback(
    async (row: MedicationEntry) => {
      if (!row.uuid || !isMedicationEntryComplete(row) || !isRowChanged(row))
        return;
      try {
        const res = await updateMedication({
          patientMedication: buildMedicationPayload(
            row,
            patientId,
            appointmentId ?? undefined
          ),
          xTENANTID,
        });
        showSnackbar(getSuccessSnackbar(res));
        await invalidate();
      } catch (err) {
        showSnackbar(getErrorSnackbar(err));
      }
    },
    [
      isRowChanged,
      patientId,
      appointmentId,
      xTENANTID,
      updateMedication,
      invalidate,
      showSnackbar,
    ]
  );

  const removeRow = useCallback(
    async (row: MedicationEntry) => {
      if (!row.uuid) {
        setRows((prev) => prev.filter((it) => it.id !== row.id));
        return;
      }
      try {
        await archiveMedication({
          patientMedicationId: row.uuid,
          status: true,
          xTENANTID,
        });
        showSnackbar(recordChangedSnackbar(MEDICATION_RECORD_NAME, "removed"));
        await invalidate();
      } catch (err) {
        showSnackbar(getErrorSnackbar(err));
      }
    },
    [xTENANTID, archiveMedication, invalidate, showSnackbar]
  );

  return {
    rows,
    draft,
    isLoading,
    isSaving: isCreating || isUpdating || isArchiving,
    updateRow,
    updateDraft,
    addDraft,
    saveRow,
    removeRow,
    isRowChanged,
  };
};
